import { useEffect, useRef, useState } from "react";
import useFetch from "../../Hooks/useFetch";
import styles from "./assets/css/About.module.css";
import heroImage from "./assets/img/about_hero.webp";
import heroImageMobile from "./assets/img/about_hero_mobile.webp";
import ImageSlider from "../../Components/ImageSlider/ImageSlider";
import CoreValues from "../../Components/CoreValues/CoreValues";
import { useLocation } from "react-router-dom";
import scrollIconMouse from "./assets/img/mouse_icon.webp";
import scrollIconArrows from "./assets/img/down_arrows.webp";
import { CSSTransition } from "react-transition-group";

const About = () => {
  const { data: premiumBrands } = useFetch("premium_brands");
  const { data: racingServices } = useFetch("racing_services");
  const [sortedPremiumBrands, setSortedPremiumBrands] = useState(null);
  const [fadeIn, setFadeIn] = useState({
    racingServiceContainer: false,
    racingServicesSlider: false,
    premiumBrands: false,
  });
  const [screenWidth, setScreenWidth] = useState(() => window.innerWidth);
  const { pathname } = useLocation();
  const racingServicesSlider = useRef();
  const racingServiceContainer = useRef();
  const premiumBrandsRef = useRef();

  useEffect(() => {
    window.addEventListener("resize", (e) => {
      setScreenWidth(e.target.innerWidth);
    });
  }, []);

  useEffect(() => {
    if (!premiumBrands) return;
    setSortedPremiumBrands(
      premiumBrands.toSorted(
        (a, b) => a.acf.premium_brands.order - b.acf.premium_brands.order
      )
    );
  }, [premiumBrands]);

  useEffect(() => {
    if (!racingServicesSlider.current) return;
    if (!racingServiceContainer.current) return;
    if (!premiumBrandsRef) return;

    const options = {
      root: null,
      rootMargin: "0px 0px 0px 0px",
      threshold: 0.5,
    };

    const premiumBrandsOptions = {
      root: null,
      rootMargin: "-290px 0px 0px 0px",
      threshold: 0,
    };

    const callback = (entries, observer) => {
      entries.forEach((entry) => {
        const sectionName = entry.target.id;
        if (sectionName !== "premiumBrands" && entry.isIntersecting) {
          setFadeIn((values) => ({ ...values, [sectionName]: true }));
          observer.unobserve(entry.target);
        } else {
          setFadeIn((values) => ({
            ...values,
            [sectionName]: entry.isIntersecting,
          }));
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);
    const premiumBrandsObserver = new IntersectionObserver(
      callback,
      premiumBrandsOptions
    );
    observer.observe(racingServicesSlider.current);
    observer.observe(racingServiceContainer.current);
    premiumBrandsObserver.observe(premiumBrandsRef.current);
  }, [pathname]);

  return (
    <>
      <section className={styles.hero}>
        <div className={styles.heroImageMobileContainer}>
          <img
            src={heroImageMobile}
            alt="technical touch hero background"
            height={384}
            width={400}
          />
        </div>
        <div className={styles.heroImageContainer}>
          <img
            src={heroImage}
            alt="technical touch hero background"
            height={771}
            width={1141}
          />
        </div>
        <div className={`${styles.heroSectionContainer} container`}>
          <div className={styles.heroText}>
            <h1>Precision Since '92</h1>
            <p>
              Technical Touch was founded by the owner Jan Berthels over 30
              years ago, back in 1992. What started out as a one-man-business
              would overtime become a well-known name in the MX/ENDURO world.
            </p>
            <p>
              In the company's early days, Jan developed his contacts and
              knowledge with KYB Suspensions in Japan and in the MX world. He
              was the personal suspension mechanic for ex-World champion MotoGP
              Kevin Schwantz and for none other than one of the greats in the MX
              world: Stefan Everts.
            </p>
            <p>
              After noticing how difficult it was to obtain OEM KYB parts, he
              put in great effort to close a deal with KYB Japan and became the
              exclusive worldwide importer for KYB detailed Motorcycle Genuine
              parts and oils. With this deal he started providing in-house
              servicing and technical support for new customers and
              distributors.
            </p>
          </div>
        </div>
        <a href={"#racing-service"} className={styles.scrollIconContainer}>
          <img
            className={styles.scrollIconMouse}
            src={scrollIconMouse}
            alt="icon of a mouse scrolling down the page"
            width={37}
            height={58}
          />
          <img
            className={styles.scrollIconArrows}
            src={scrollIconArrows}
            alt="arrow icon pointing down the page"
            width={23}
            height={24}
          />
        </a>
      </section>
      <section id="racing-service" className={styles.racingService}>
        <div
          ref={racingServiceContainer}
          id="racingServiceContainer"
          className={`${styles.racingServiceContainer} ${
            fadeIn.racingServiceContainer && styles.fadeIn
          } container`}
        >
          <h2>Racing Service</h2>
          <p>
            Besides having its own service centre with in-house technicians,
            Technical Touch provides official KYB Racing Service for Factory KYB
            teams and teams who rely on our KYB Factory Kit Suspension in the
            MXGP, MX2 and EMX series.
          </p>
          <p>
            With more than 15 years of experience in distribution, R&D and
            Racing service, Technical Touch was ready for the next step! In 2007
            Technical touch USA was founded to supply the North – and South of
            America with KYB detailed Motorcycle Genuine parts and oils.
          </p>
        </div>
        <div
          ref={racingServicesSlider}
          id="racingServicesSlider"
          className={`
          ${styles.racingServicesSlider} 
          ${fadeIn.racingServicesSlider && styles.fadeIn}
          `}
        >
          {racingServices?.length > 0 && (
            <ImageSlider
              autoplay={true}
              arrows={true}
              infinite={true}
              slidesToShow={
                screenWidth >= 1024 ? 3 : screenWidth >= 530 ? 2 : 1
              }
              sliderClassName={"racing-services"}
              images={[
                ...racingServices
                  .sort(
                    (a, b) =>
                      a.acf.racing_services.order - b.acf.racing_services.order
                  )
                  .map((service) => service.acf.racing_services.image),
              ]}
            />
          )}
        </div>
      </section>
      <section className={styles.premiumBrands}>
        <div className={`${styles.premiumBrandsContainer} container`}>
          <div
            id="premiumBrands"
            className={styles.brandLogos}
            ref={premiumBrandsRef}
          >
            {sortedPremiumBrands &&
              sortedPremiumBrands
                .filter(
                  (brand) => brand.acf.premium_brands.show_on_about_us_page
                )
                ?.map((brand, index) => (
                  <CSSTransition
                    key={index}
                    timeout={750}
                    in={fadeIn.premiumBrands}
                    classNames={{
                      enter: styles.brandLogoEnter,
                      enterActive: styles.brandLogoEnterActive,
                      enterDone: styles.brandLogoEnterDone,
                      exit: styles.brandLogoExit,
                      exitActive: styles.brandLogoExitActive,
                      exitDone: styles.brandLogoExitDone,
                    }}
                    style={{
                      transitionDelay: `${index * 0.2}s`,
                    }}
                  >
                    <img
                      className={styles.brandLogo}
                      src={brand.acf.premium_brands.brand_logo.sizes.medium}
                      alt={brand.title.rendered + " logo"}
                      height={
                        brand.acf.premium_brands.brand_logo.sizes[
                          "medium-height"
                        ]
                      }
                      width={
                        brand.acf.premium_brands.brand_logo.sizes[
                          "medium-width"
                        ]
                      }
                    />
                  </CSSTransition>
                ))}
          </div>
          <h2>Premium Brands</h2>
          <p>
            Next to being a KYB distributor, we also became the European
            Importer for Hinson Clutch Racing Components and started supplying
            the European market with Yoshimura Exhausts, ODI Grips and X-Trig
            triple clamps and racing components.
          </p>
          <p>
            In 2018 we were ready to take things to the next level and started
            providing several European brands with KYB OEM Suspension and
            products. We can proudly say we work together with Yamaha, Sherco,
            Stark, Beta, etc., …
          </p>
        </div>
      </section>
      <CoreValues />
    </>
  );
};
export default About;
